import { createContext, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { onAccessRefused, onPermissionsChanged } from '../api';
import type { AccessRefusal } from '../api';
import ErrorScreen from '../core-components/theme/components/ErrorScreen';
import { LoadingState } from '../core-components/theme/components/Screen/ScreenStates';
import { StateArea } from '../core-components/theme/components/Screen/StateArea';

import { ACCESS_POLICY } from './policy';
import type { AccessPolicy } from './policy';
import { accessKeys } from './query-keys';
import { meAccessQueryOptions } from './queryOptions';
import { reportAccess } from './reporting';
import type { MeAccess } from './types';

export interface AccessContextValue {
  readonly me: MeAccess;
  readonly policy: AccessPolicy;
}

export const AccessContext = createContext<AccessContextValue | null>(null);

const REREAD_AFTER_REFUSAL_MS = 60_000;

const rereadOnRefusal = (queryClient: QueryClient) => {
  let lastReread = Number.NEGATIVE_INFINITY;
  return (refusal: AccessRefusal): void => {
    reportAccess({ kind: 'refused-after-allow', url: refusal.url, code: refusal.code });
    const now = Date.now();
    if (now - lastReread < REREAD_AFTER_REFUSAL_MS) return;
    lastReread = now;
    void queryClient.invalidateQueries({ queryKey: accessKeys.all });
  };
};

interface AccessProviderProps {
  readonly children: ReactNode;
  readonly policy?: AccessPolicy;
  readonly onAccessChanged?: () => void;
}

export const AccessProvider = ({ children, policy = ACCESS_POLICY, onAccessChanged }: AccessProviderProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: me, isError, refetch } = useQuery(meAccessQueryOptions());

  useEffect(
    () => onPermissionsChanged(() => void queryClient.invalidateQueries({ queryKey: accessKeys.all }).then(onAccessChanged)),
    [queryClient, onAccessChanged],
  );
  useEffect(() => onAccessRefused(rereadOnRefusal(queryClient)), [queryClient]);

  const value = useMemo<AccessContextValue | null>(() => (me === undefined ? null : { me, policy }), [me, policy]);

  if (value !== null) return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
  if (isError) {
    return (
      <StateArea placement="window">
        <ErrorScreen onRetry={() => void refetch()} />
      </StateArea>
    );
  }
  return <LoadingState label={t('access.loading', 'Loading your access')} placement="window" />;
};
